import { env } from 'constants/env';
import { FullStory } from 'V2/Analytics';

import { ConfigType, HttpType } from './types';
import { createAxiosInstance } from './AxiosConfig';
import { createRequestInterceptor } from './AxiosConfig/interceptors';
import { LogToService } from 'V2/Utilities/Logger';

declare global {
  interface Window {
    AwsWafIntegration: any;
  }
}

const axiosInstance = createAxiosInstance({
  baseURL: env.apiHost,
  // The applicant_auth cookie is the credential for the apply flow (DLC-1725).
  withCredentials: true,
  onRequestFulfilled: createRequestInterceptor(async (request) => {
    try {
      const awsWafToken: string =
        window.AwsWafIntegration && (await window.AwsWafIntegration.getToken());

      if (!!awsWafToken) {
        request.headers.set('X-Aws-Waf-Token', awsWafToken);
      }
    } catch (error: any) {
      console.error('Error: ', error);
      LogToService.captureException({
        errorType: 'client',
        message: error.message,
        thread: 'Aws-Waf-Token',
        error: new Error(error),
      });
    }
  }),
});

/**
 * The applicant session is the applicant_auth cookie (DLC-1725). The legacy header is sent only while a
 * token is actually stored (the Choice fallback): reznor decodes the header whenever it is present, so an
 * empty value is a malformed JWT and fails the request even when a valid cookie is attached.
 */
const getAuthHeaders = (): Record<string, string> => {
  const portalToken = localStorage.getItem('authToken');
  if (portalToken) {
    return { Authorization: `Bearer ${portalToken}` };
  }

  const applicantToken = sessionStorage.getItem('authToken');
  if (applicantToken) {
    return { 'Clearcover-Applicant-Authorization-Token': applicantToken };
  }

  return {};
};

const getDefaultHeaders = () => ({
  'Clearcover-Fullstory-Identity': FullStory.getCurrentSession() || '',
  ...getAuthHeaders(),
  'Clearcover-ClickId': sessionStorage.getItem('clickId') || '',
});

export const http: HttpType = {
  get: (config: ConfigType) =>
    axiosInstance.request({
      ...config,
      method: 'get',
      responseType: 'json',
      url: config.url,
      baseURL: env.apiHost,
      withCredentials: true,
      headers: {
        ...getDefaultHeaders(),
        ...config.headers,
      },
    }),
  post: (config: ConfigType) =>
    axiosInstance.request({
      ...config,
      method: 'post',
      responseType: 'json',
      url: config.url,
      baseURL: env.apiHost,
      withCredentials: true,
      headers: {
        ...getDefaultHeaders(),
        ...config.headers,
      },
    }),
};
